// NotFoundPage.js

import React from "react";
import { Box, Typography, Button, Container } from "@mui/material";
import { useNavigate } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import SEO from "./components/SEO";

export default function NotFoundPage() {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="Not Found"
        description="That page doesn't exist. Head back to the Premier League Scorigami heatmap to explore every PL scoreline."
        path="/"
        noindex
      />
      <Container maxWidth="md" sx={{ py: { xs: 4, sm: 8 } }}>
      <Box
        sx={{
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
        }}
      >
        {/* 404 Icon */}
        <SearchOffIcon
          sx={{
            fontSize: { xs: 80, sm: 120 },
            color: "#39d0ff",
            opacity: 0.8,
          }}
        />

        {/* 404 Text */}
        <Typography
          variant="h1"
          sx={{
            background: "linear-gradient(90deg, #39d0ff, #6f5df5)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontSize: { xs: "4rem", sm: "6rem", md: "8rem" },
            fontWeight: 700,
            lineHeight: 1,
            mb: 2,
          }}
        >
          404
        </Typography>

        {/* Page Not Found */}
        <Typography
          variant="h4"
          sx={{
            color: "#fff",
            mb: 2,
            fontSize: { xs: "1.5rem", sm: "2rem" },
          }}
        >
          Page Not Found
        </Typography>

        {/* Description */}
        <Typography
          variant="body1"
          sx={{
            color: "#b0b0b0",
            maxWidth: 600,
            lineHeight: 1.8,
            fontSize: { xs: "1rem", sm: "1.1rem" },
            mb: 3,
          }}
        >
          The page you're looking for doesn't exist. It might have been moved, deleted, 
          or the URL might be incorrect. Don't worry though - you can explore Premier League 
          Scorigami history from our home page!
        </Typography>

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Button
            variant="contained"
            onClick={() => navigate("/")}
            startIcon={<HomeIcon />}
            sx={{
              background: "linear-gradient(90deg, #39d0ff, #6f5df5)",
              color: "#fff",
              px: 4,
              py: 1.5,
              fontSize: "1.1rem",
              "&:hover": {
                background: "linear-gradient(90deg, #6f5df5, #39d0ff)",
              },
            }}
          >
            Go to Home
          </Button>

          <Button
            variant="outlined"
            onClick={() => navigate("/about")}
            sx={{
              borderColor: "#39d0ff",
              color: "#39d0ff",
              px: 4,
              py: 1.5,
              fontSize: "1.1rem",
              "&:hover": {
                borderColor: "#6f5df5",
                color: "#6f5df5",
                backgroundColor: "rgba(111,93,245,0.1)",
              },
            }}
          >
            Learn More
          </Button>
        </Box>

        {/* Additional Help */}
        <Box sx={{ mt: 4, p: 3, background: "rgba(25,25,30,0.6)", borderRadius: 2, width: "100%" }}>
          <Typography variant="h6" sx={{ color: "#39d0ff", mb: 2 }}>
            Looking for something specific?
          </Typography>
          <Typography sx={{ color: "#b0b0b0", lineHeight: 1.8, mb: 2 }}>
            Here are some helpful links:
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "center" }}>
            <Button
              variant="text"
              onClick={() => navigate("/")}
              sx={{ color: "#b0b0b0", "&:hover": { color: "#39d0ff" } }}
            >
              Interactive Scorigami Heatmap
            </Button>
            <Button
              variant="text"
              onClick={() => navigate("/about")}
              sx={{ color: "#b0b0b0", "&:hover": { color: "#39d0ff" } }}
            >
              About Premier League Scorigami
            </Button>
            <Button
              variant="text"
              onClick={() => navigate("/faq")}
              sx={{ color: "#b0b0b0", "&:hover": { color: "#39d0ff" } }}
            >
              Frequently Asked Questions
            </Button>
          </Box>
        </Box>
      </Box>
      </Container>
    </>
  );
}